import styled from 'styled-components';
import type { ConsultationProductListModel } from 'autogen/swagger/all';
import { convertTimeSpanToString, dateTimeFormat } from 'helpers/DateTimeConverter';
import { Spacing4, Spacing8, Spacing12, Spacing6 } from 'autogen/design-tokens/tokens';
import { useTranslation } from 'react-i18next';
import { FreeLabel, NextEvent } from 'autogen/translation-keys/trans-website-booking-consultationlist-consultationitem';
import Tippy from '@tippyjs/react';
import { Typography } from 'components/generic/typography';
import { InBooth } from 'autogen/translation-keys/trans-website-booking-consultationproduct';
import { useGetBookingTypeTrans } from '../shared/useGetBookingTypeTrans';
import formatTitle from '../shared/formatTitle';
import Icon from 'components/Icon';
import ContentCard from 'components/ContentCard';
import { useMemo } from 'react';
import { mapResponsiveImageModelToImageProps } from 'components/generic/Image/helpers/mapToImageProps';
import { TitleWithPartnerName } from 'autogen/translation-keys/trans-website-booking-shared';

const AdditionalInfo = styled.div`
    display: grid;
    gap: ${Spacing8};
`;

const TimePriceInfo = styled.div`
    display: grid;
    gap: ${Spacing6};
    grid-template-columns: repeat(2, max-content);
`;

const TooltipContentContainer = styled.div`
    max-width: 240px;
`;

const PriceInfoContainer = styled.span`
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    z-index: 1;
`;

const InfoCircle = styled(Icon)`
    margin-left: ${Spacing4};
`;

export interface IConsultationProductItem {
    item: ConsultationProductListModel;
}

function formatEventStartTime(startTime: string) {
    const formattedStartTime = dateTimeFormat({
        dateTime: startTime,
        options: { weekday: 'long', year: '2-digit', month: 'long', day: 'numeric' }
    }).replace(' den ', ' d. ');

    return formattedStartTime;
}

export default function ConsultationProductItem({ item }: IConsultationProductItem): JSX.Element {
    const {
        linkAction,
        image,
        duration,
        title,
        nextAvailableBookingSlot,
        externalPartner,
        bookingType,
        suggestedPriceText,
        priceDisclaimer,
        category,
        inBooth,
        isVirtual
    } = item;

    const { t } = useTranslation();
    const { startTime } = nextAvailableBookingSlot || {};
    const { name: externalPartnerName } = externalPartner || {};
    const imageProps = useMemo(() => (image ? mapResponsiveImageModelToImageProps(image) : undefined), [image]);
    const linkProps = linkAction ? { action: { $type: 'LinkActionType', action: linkAction } } : undefined;
    const tagProps = inBooth ? [category.name, t(InBooth)] : [category.name];

    const productTitle = formatTitle({
        productName: title,
        externalPartnerName,
        translation: t(TitleWithPartnerName)
    });

    return (
        <ContentCard
            image={imageProps}
            link={linkProps}
            heading={productTitle}
            subheading={useGetBookingTypeTrans({ isVirtual, bookingType })}
            tags={tagProps}
        >
            <AdditionalInfo>
                {bookingType === 'Event' && startTime && (
                    <Typography.Body size="M">
                        {t(NextEvent)} {formatEventStartTime(startTime)}
                    </Typography.Body>
                )}
                <TimePriceInfo>
                    {priceDisclaimer ? (
                        <Tippy
                            maxWidth={230}
                            theme="plain-tooltip"
                            placement="bottom"
                            content={
                                <TooltipContentContainer>
                                    <Typography.Body size="S" as="span">
                                        *{priceDisclaimer}
                                    </Typography.Body>
                                </TooltipContentContainer>
                            }
                            touch="hold"
                            popperOptions={{
                                modifiers: [
                                    {
                                        name: 'flip',
                                        enabled: false
                                    }
                                ]
                            }}
                        >
                            <PriceInfoContainer>
                                <Typography.Body size="M" as="span" strong>
                                    {suggestedPriceText}*
                                </Typography.Body>
                                <InfoCircle type="Information" size={16} />
                            </PriceInfoContainer>
                        </Tippy>
                    ) : (
                        <Typography.Body size="M" as="span" strong>
                            {suggestedPriceText ? suggestedPriceText : t(FreeLabel)}
                        </Typography.Body>
                    )}

                    <Typography.Body size="M" as="span">
                        &#40;
                        {convertTimeSpanToString({
                            timeSpan: duration
                        }).trim()}
                        &#41;
                    </Typography.Body>
                </TimePriceInfo>
            </AdditionalInfo>
        </ContentCard>
    );
}
